type PatternParam<Pattern extends string> = Pattern extends `${infer Head}/${infer Tail}`
  ? PatternParam<Head> | PatternParam<Tail>
  : Pattern extends `:${infer Name}`
    ? Name
    : never;

const fillPattern = <Pattern extends string>(
  pattern: Pattern,
  params: Record<PatternParam<Pattern>, string>
): string => {
  const values: Readonly<Record<string, string>> = params;
  const segments = pattern
    .split('/')
    .map(segment => (segment.startsWith(':') ? encodeURIComponent(values[segment.slice(1)]) : segment))
    .filter(segment => segment !== '');
  return `/${segments.join('/')}`;
};

export const PASSCODE_ROUTES = {
  login: '/Login',
  legacyCodeLogin: '/login/code',
  legacyForgotPassword: '/ForgotPasswordV2',
  legacyResetPassword: '/resetPassword',
  legacyAcceptInvitation: '/acceptInvitation',
} as const;

export const LEGACY_LOGIN_PATHS: readonly string[] = [
  PASSCODE_ROUTES.legacyCodeLogin,
  PASSCODE_ROUTES.legacyForgotPassword,
  PASSCODE_ROUTES.legacyResetPassword,
  PASSCODE_ROUTES.legacyAcceptInvitation,
];

export const ONBOARDING_ROUTES = {
  wizard: '/onboarding',
  start: '/start',
} as const;

export const ACCOUNT_ROUTES = {
  contact: '/profile/contact',
  password: '/profile/password',
  root: '/profile',
  pictureAndDashboard: '/profile/picture',
} as const;

const CTL_BASE = '/profile/notifications';

export const CTL_PATTERNS = {
  plantPolicy: '/plants/:plantId/notification-policy',
  plantRouting: '/plants/:plantId/notification-routing',
  siteSettings: '/plants/:plantId/site-settings',
  landing: '/i/:token',
  legacyIssue: '/issues/:issueId',
} as const;

export const CTL_ROUTES = {
  settings: CTL_BASE,
  quietHours: `${CTL_BASE}/quiet-hours`,
  recent: `${CTL_BASE}/recent`,
  issuesList: '/tickets',
  plantPolicy: (plantId: string): string => fillPattern(CTL_PATTERNS.plantPolicy, { plantId }),
  plantRouting: (plantId: string): string => fillPattern(CTL_PATTERNS.plantRouting, { plantId }),
  siteSettings: (plantId: string): string => fillPattern(CTL_PATTERNS.siteSettings, { plantId }),
  landing: (token: string): string => fillPattern(CTL_PATTERNS.landing, { token }),
  issue: (issueId: string): string => fillPattern(CTL_PATTERNS.legacyIssue, { issueId }),
} as const;

const USER_CENTER_BASE = '/user-center';

export const USER_CENTER_ROUTES = {
  base: USER_CENTER_BASE,
  people: `${USER_CENTER_BASE}/people`,
  peopleAtPlant: (plantId: string): string =>
    `${USER_CENTER_BASE}/plants/${encodeURIComponent(plantId)}/people`,
  person: (userId: string): string => `${USER_CENTER_BASE}/people/${userId}`,
  library: `${USER_CENTER_BASE}/library`,
  plants: `${USER_CENTER_BASE}/plants`,
} as const;

const EQUIPMENT_LIBRARY_BASE = '/equipment-library';

export const CTL_CONFIG_PATTERNS = {
  library: EQUIPMENT_LIBRARY_BASE,
  plantTypes: `${EQUIPMENT_LIBRARY_BASE}/plant-types`,
  database: `${EQUIPMENT_LIBRARY_BASE}/database`,
  type: `${EQUIPMENT_LIBRARY_BASE}/:code`,
  typeEdit: `${EQUIPMENT_LIBRARY_BASE}/:code/edit`,
  scada: '/plants/:plantId/equipment-library/scada',
  machines: '/plants/:plantId/equipment',
  machine: '/plants/:plantId/equipment/:entryId',
} as const;

export const CTL_CONFIG_ROUTES = {
  library: CTL_CONFIG_PATTERNS.library,
  plantTypes: CTL_CONFIG_PATTERNS.plantTypes,
  database: CTL_CONFIG_PATTERNS.database,
  type: (code: string): string => fillPattern(CTL_CONFIG_PATTERNS.type, { code }),
  typeEdit: (code: string): string => fillPattern(CTL_CONFIG_PATTERNS.typeEdit, { code }),
  scada: (plantId: string): string => fillPattern(CTL_CONFIG_PATTERNS.scada, { plantId }),
  machines: (plantId: string): string => fillPattern(CTL_CONFIG_PATTERNS.machines, { plantId }),
  machine: (plantId: string, entryId: string): string => fillPattern(CTL_CONFIG_PATTERNS.machine, { plantId, entryId }),
} as const;
